
export function requestAnimationDebounce<T extends Function>(fn: T) {
  let timer: number = -1
  return function (this: any, ...args: any) {
    cancelAnimationFrame(timer)
    timer = requestAnimationFrame(() => {
      fn.apply(this, args)
    })
  }
}

export const isEllipsisHeight = (dom: any, maxWidth: any, parentNode: any) => {
  const checkDom = dom.cloneNode();
  const computedStyle = window.getComputedStyle(dom);
  let parent, flag;
  checkDom.style.cssText = `
  diaplay: block;
  max-width: initial;
  width: ${dom.offsetWidth}px;
  height: auto;
  overflow: unset;
  position: absolute;
  top: 0;
  zIndex: -99;
  opacity: 0;
  whiteSpace: nowrap;
  font-family: ${computedStyle.fontFamily};
  font-size: ${computedStyle.fontSize};
  display: block;
  `;

  checkDom.innerHTML = dom.innerHTML;

  parent = document.body;

  parent.appendChild(checkDom);
  const autoHeight = checkDom.getBoundingClientRect().height;
  const realHeight = maxWidth || dom.getBoundingClientRect().height;
  flag = autoHeight > realHeight;
  parent.removeChild(checkDom);
  return flag;
};
/**
 * 内容判断是否超出容器
 */
export function isEllipsis(dom: HTMLElement, maxWidth?: number) {
  const computedStyle = window.getComputedStyle(dom)

  // 检测是否使用了多行省略 (-webkit-line-clamp)
  const lineClamp = computedStyle.webkitLineClamp || computedStyle.getPropertyValue('-webkit-line-clamp')
  const boxOrient = computedStyle.webkitBoxOrient || computedStyle.getPropertyValue('-webkit-box-orient')
  const isMultiLineEllipsis = lineClamp && lineClamp !== 'none' && boxOrient === 'vertical'

  if (isMultiLineEllipsis) {
    // 多行省略：比较 scrollHeight 和 clientHeight
    return dom.scrollHeight > dom.clientHeight
  }

  // 单行省略：原有逻辑，比较宽度
  const checkDom = dom.cloneNode() as HTMLElement
  let parent, flag
  // display: ${computedStyle.display}; 避免 影响flex布局
  checkDom.style.cssText = `
    display: ${computedStyle.display};
    max-width: initial;
    width: auto;
    height: ${dom.offsetHeight}px;
    overflow: unset;
    position: absolute;
    zIndex: -1;
    opacity: 0;
    whiteSpace: nowrap;
    font-family: ${computedStyle.fontFamily};
    font-size: ${computedStyle.fontSize}
    `
  checkDom.classList.add('overtip-copy-dom')

  checkDom.innerHTML = dom.innerHTML

  parent = document.body

  parent.appendChild(checkDom)
  const autoWidth = checkDom.getBoundingClientRect().width
  const realWidth = maxWidth || dom.getBoundingClientRect().width
  flag = autoWidth > realWidth
  parent.removeChild(checkDom)
  return flag
}

export type DebounceHandler<T> = {
  (...h: any): T
  clear: () => void
}
export function debounce<T>(wait: number, fn: (...h: any) => T) {
  let timer: any

  const handleFn = function (this: any, ...args: any) {
    clearTimeout(timer)
    timer = setTimeout(() => {
      fn.apply(this, args)
    }, wait);
  }
  handleFn.clear = () => {
    clearTimeout(timer)
    return handleFn
  }
  return handleFn
}

export function throttle(time: number, handleFn: (...h: any) => any) {
  let starTime = -Infinity
  const dd = debounce(time, handleFn)
  return function tt(this: any, ...arg: any) {
    const nowTime = performance.now()
    if (nowTime - starTime >= time) {
      handleFn.call(this, ...arg)
      starTime = nowTime
    } else {
      dd.call(this, ...arg)
    }
  }
}
export function isPromise<T>(val: unknown): val is Promise<T> {
  return Object.prototype.toString.call(val) === '[object Promise]'
}
export type RafInterval = {
  (): void
  clear: () => void
  start: (h?: any) => void
}
export function getRaf(cb: (h?: any) => any, time = 0): RafInterval {
  let rafId: number | null = null
  let keep = true
  let startTime = -Infinity
  const thresholdTime = time
  async function rafFn(this: any, ...arg: any) {
    let currentTime = performance.now()
    if (currentTime - startTime < thresholdTime) {
      cancelAnimationFrame(rafId as number)
      rafId = requestAnimationFrame(rafFn.bind(this, ...arg))
      return
    }
    startTime = performance.now()
    try {
      const cbRes = cb.apply(this, arg)
      if (isPromise(cbRes)) {
        await cbRes
      }
    } catch (err) {
      console.error(err)
    }
    if (!keep) return
    rafId = requestAnimationFrame(rafFn.bind(this, ...arg))
  }
  rafFn.clear = () => {
    keep = false
    if (rafId) cancelAnimationFrame(rafId)
  }
  rafFn.start = (h?: any) => {
    rafFn.clear()
    keep = true
    rafFn(h)
  }
  return rafFn
}

/**
 * @description: 处理跟随tip或者弹窗，滚动时抖动问题
 * @return {*}
 */
export function addScrollRefresh(fn: (e: Event) => void): () => void {
  const raf = getRaf(fn)
  let isStart = false
  const scrollStart = (e: Event) => {
    if (!isStart) {
      isStart = true
      raf.start(e)
    }
  }
  const scrollStop = debounce(320, () => {
    raf.clear()
    isStart = false
  })
  const scrollCb = (e: Event) => {
    scrollStart(e)
    scrollStop(e)
  }
  window.addEventListener('resize', scrollCb)
  window.addEventListener('scroll', scrollCb, { capture: true })
  return () => {
    window.removeEventListener('scroll', scrollCb, { capture: true })
    window.removeEventListener('resize', scrollCb)
    raf.clear()
  }
}

/**
 * 格式化营业时间显示
 * @param startTime 开始时间（ISO 8601格式或其他日期时间字符串）
 * @param endTime 结束时间（ISO 8601格式或其他日期时间字符串）
 * @param nextDayText 翌日文案（国际化文本，如"翌日"或"次日"）
 * @returns 格式化后的营业时间字符串，如 "10:00 ~ 23:00" 或 "10:00 ~ 翌日 02:00"
 */
export function formatBusinessHours(
  startTime: string | undefined | null,
  endTime: string | undefined | null,
  nextDayText: string
): string {
  if (!startTime || !endTime) {
    return '-';
  }

  try {
    const startDate = new Date(startTime);
    const endDate = new Date(endTime);

    // 验证日期是否有效
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      return '-';
    }

    // 格式化为 HH:mm
    const formatTime = (date: Date): string => {
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${hours}:${minutes}`;
    };

    const startFormatted = formatTime(startDate);
    const endFormatted = formatTime(endDate);

    // 检查日期是否跨天（endDate的日期比startDate大）
    const startDay = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const endDay = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    const isNextDay = endDay.getTime() > startDay.getTime();

    if (isNextDay) {
      return `${startFormatted} ~ ${nextDayText} ${endFormatted}`;
    } else {
      return `${startFormatted} ~ ${endFormatted}`;
    }
  } catch (error) {
    console.error('Error formatting business hours:', error);
    return '-';
  }
}
